import React, {Component} from 'react';
import PropTypes from 'prop-types';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import moment from 'moment-timezone';
import SHGlobals from 'sh-globals';
import DateUtils from 'date-utils';
import TimeUtils from 'time-utils';
import DatePicker from 'date-picker-component';
import DateTimeRangePicker from 'date-time-range-picker-component';
import Label from 'label-component';

class DateTimePickers extends Component {
    constructor(props) {
        super(props);

        const {
            transientStarts,
            transientEnds,
            monthlyStarts
        } = props;

        const transientStartsMoment = moment(transientStarts, SHGlobals.SERVER_DATE_TIME_FORMAT),
            transientEndsMoment = moment(transientEnds, SHGlobals.SERVER_DATE_TIME_FORMAT);

        this._autoUpdated = false;
        this._onTransientChange = this._onTransientChange.bind(this);
        this._onMonthlyChange = this._onMonthlyChange.bind(this);
        this.state = {
            startTooltipType: 'none',
            startTooltipMessage: null,
            endTooltipType: 'none',
            endTooltipMessage: null,
            transientStart: transientStartsMoment.isValid() ? transientStartsMoment : moment(),
            transientEnd: transientEndsMoment.isValid() ? transientEndsMoment : moment(),
            monthlyStart: (monthlyStarts) ? moment(monthlyStarts) : moment(),
            isValidStart: true,
            isValidEnd: true
        };
        this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
    }

    componentDidMount() {
        const {
            shouldUpdateStaleStart
        } = this.props;

        if (shouldUpdateStaleStart) {
            setTimeout(() => {
                this._checkStaleStart();
            }, 250);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const onChangeState = {
            ...this.state
        };

        if (this._autoUpdated) {
            onChangeState.autoUpdated = true;
            this._autoUpdated = false;
        }

        this.props.onChange(onChangeState);
    }

    _checkStaleStart() {
        const {
            transientStarts,
            transientEnds,
            monthlyStarts,
            isMonthly
        } = this.props;
        const checkDate = (isMonthly) ? monthlyStarts : transientStarts;
        const {
            isNewStart,
            newStart
        } = TimeUtils.checkStaleStart(checkDate, window.currentCityDateTime, window.currentCityTimezone);

        if (isNewStart) {
            const newState = {
                isValidStart: true,
                startTooltipType: 'date',
                startTooltipMessage: 'The selected start was in the past so we updated it for you.'
            };

            this._autoUpdated = true;

            if (isMonthly) {
                newState.monthlyStart = newStart;
            } else {
                newState.transientStart = newStart;

                const transientStartsWithNewStartDate = moment(transientStarts)
                    .date(newStart.date())
                    .month(newStart.month())
                    .year(newStart.year());

                if (newStart.isBefore(transientStartsWithNewStartDate)) {
                    newState.transientStart = transientStartsWithNewStartDate;
                    newState.transientEnd = moment(transientEnds)
                        .date(newStart.date())
                        .month(newStart.month())
                        .year(newStart.year());

                    // in case transientEnd time was midnight or after, we could be off a day now
                    // if so add a day to transientEnd
                    if (moment(transientEnds).dayOfYear() > moment(transientStarts).dayOfYear()) {
                        newState.transientEnd.add(1, 'day');
                    }
                } else {
                    newState.transientStart = newStart;
                    newState.transientEnd = newStart.clone().add(3, 'hours');
                }
            }

            this.setState(newState);
        } else {
            this.setState({
                isValidStart: true
            });
        }
    }

    _onTransientChange({startDate, startTime, endDate, endTime}) {
        const starts = DateUtils.combineDateAndTime(DateUtils.getAsMoment(startDate), TimeUtils.getAsMoment(startTime));
        const ends = DateUtils.combineDateAndTime(DateUtils.getAsMoment(endDate), TimeUtils.getAsMoment(endTime));
        const newState = {
            startTooltipType: 'none',
            startTooltipMessage: null,
            transientStart: starts,
            transientEnd: ends,
            monthlyStart: starts,
            isValidStart: true
        };

        if (!SHGlobals.isAdmin) {
            const {
                isNewStart
            } = TimeUtils.checkStaleStart(starts.clone(), window.currentCityDateTime, window.currentCityTimezone);

            if (isNewStart) {
                this.setState({
                    startTooltipType: 'time',
                    startTooltipMessage: 'Cannot search for more than 30 minutes in the past.',
                    transientStart: starts,
                    transientEnd: ends,
                    monthlyStart: starts,
                    isValidStart: false
                });

                return false;
            } else {
                if (ends.isBefore(starts)) {
                    newState.isValidStart = false;
                } else {
                    newState.isValidStart = true;
                }
            }
        }

        this.setState(newState);
    }

    _onMonthlyChange(selectedDate) {
        const selectedDateMoment = DateUtils.getAsMoment(selectedDate);
        const transientStart = selectedDateMoment.clone().hour(moment().hour());

        this.setState({
            transientStart: transientStart,
            transientEnd: transientStart.clone().add(3, 'hours'),
            monthlyStart: selectedDateMoment,
            isValidStart: true
        });
    }

    _renderTransient() {
        const {
            startDateLabel,
            endDateLabel,
            tooltipPosition,
            useCalendarWithShim
        } = this.props;
        const {
            transientStart,
            transientEnd,
            startTooltipType,
            startTooltipMessage,
            endTooltipType,
            endTooltipMessage
        } = this.state;

        return (
            <DateTimeRangePicker
                className="transient-picker"
                startDateLabel={startDateLabel}
                startTimeLabel=""
                endDateLabel={endDateLabel}
                endTimeLabel=""
                startDate={transientStart.format(SHGlobals.DESKTOP_DATE_FORMAT)}
                startTime={transientStart.format(SHGlobals.DESKTOP_TIME_FORMAT)}
                endDate={transientEnd.format(SHGlobals.DESKTOP_DATE_FORMAT)}
                endTime={transientEnd.format(SHGlobals.DESKTOP_TIME_FORMAT)}
                endTimeBump={180}
                onStartChange={this._onTransientChange}
                onEndChange={this._onTransientChange}
                startTooltipType={startTooltipType}
                startTooltipMessage={startTooltipMessage}
                endTooltipType={endTooltipType}
                endTooltipMessage={endTooltipMessage}
                tooltipPosition={tooltipPosition}
                useTetherShim={useCalendarWithShim}
                updateTooltipPositionOnMove
                controlled
            />
        );
    }

    _renderMonthly() {
        const {
            monthlyStartDateLabel
        } = this.props;
        const {
            monthlyStart
        } = this.state;

        return (
            <div className="DateTimePicker-start monthly-picker">
                <Label className="monthly-label">{monthlyStartDateLabel}</Label>
                <DatePicker
                    useIcon
                    calendarClassName="calendar-start"
                    selectedDate={monthlyStart.format(SHGlobals.DESKTOP_DATE_FORMAT)}
                    onChange={this._onMonthlyChange}
                    disablePast
                />
            </div>
        );
    }

    // Temp method to get transient state data for change reservations modal which is non-React
    getTransientData() {
        return {
            starts: this.state.transientStart,
            ends: this.state.transientEnd
        };
    }

    render() {
        return (
            <div className="DateTimePicker">
                {(this.props.isMonthly)
                    ? this._renderMonthly()
                    : this._renderTransient()
                }
            </div>
        );
    }
}

DateTimePickers.propTypes = {
    isMonthly: PropTypes.bool.isRequired,
    /** A datetime in the format YYYY-MM-DDTHH:mm. */
    transientStarts: PropTypes.string,
    /** A datetime in the format YYYY-MM-DDTHH:mm. */
    transientEnds: PropTypes.string,
    /** A date in the format YYYY-MM-DD. */
    monthlyStarts: PropTypes.string,
    startDateLabel: PropTypes.string,
    endDateLabel: PropTypes.string,
    monthlyStartDateLabel: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    tooltipPosition: PropTypes.oneOf(['top', 'bottom']),
    shouldUpdateStaleStart: PropTypes.bool,
    useCalendarWithShim: PropTypes.bool
};

DateTimePickers.defaultProps = {
    transientStarts: null,
    transientEnds: null,
    monthlyStarts: null,
    startDateLabel: '',
    endDateLabel: (!SHGlobals.isMobile) ? 'to' : '',
    monthlyStartDateLabel: 'Monthly Parking Beginning',
    tooltipPosition: 'bottom',
    shouldUpdateStaleStart: true
};

export default DateTimePickers;



// WEBPACK FOOTER //
// ./spothero/static/main/js/datetime/components/common/DateTimePickers.jsx